import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Navigation } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import translations from '@/data/translations';

export default function MapSection() {
  const { lang } = useLanguage();
  const t = translations[lang].map;

  return (
    <section id="map" data-testid="map-section" className="py-16 sm:py-24 bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <span className="text-[#D48C57] text-xs font-medium tracking-[0.2em] uppercase">
            {t.sectionTag}
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A2520] mt-3 tracking-tight">
            {t.title}
          </h2>
          <p className="text-[#5C6A61] text-base mt-3 max-w-2xl">{t.subtitle}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="relative rounded-2xl overflow-hidden border border-[#E5DFD3] bg-white"
        >
          <iframe
            data-testid="google-map-embed"
            src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d1500!2d80.1707792!3d13.2220992!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMTPCsDEzJzE5LjYiTiA4MMKwMTAnMTQuOCJF!5e1!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Lakshmi Nivasa Location"
            className="w-full h-[300px] sm:h-[400px]"
          />

          {/* Address overlay */}
          <div className="absolute bottom-4 left-4 right-4 sm:right-auto">
            <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 border border-[#E5DFD3] shadow-lg max-w-sm">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#2C4C3B] flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-serif font-bold text-sm text-[#1A2520]">{t.projectName}</p>
                  <p className="text-[#5C6A61] text-xs mt-0.5 leading-relaxed">{t.address}</p>
                </div>
              </div>
              <a
                href="https://maps.app.goo.gl/8hfQXEfVrK1oqgND8"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="map-directions-link"
                className="mt-3 flex items-center justify-center gap-2 w-full bg-[#2C4C3B] hover:bg-[#213A2D] text-white py-2.5 rounded-lg text-xs font-semibold transition-all duration-200"
              >
                <Navigation className="w-3.5 h-3.5" />
                {t.getDirections}
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
